import {
  NotificationGroup,
  addNotificationsAtom,
} from 'components/atomised-notifications';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { HTTPError } from 'ky';
import { usePolling } from 'lib/hooks/usePolling';
import { subscribe } from 'lib/pusher';
import { api } from 'lib/request/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  appendUploadedRowsAtom,
  applyDryRunAtom,
  batchDefaultAtom,
  curationRowsAtom,
  dryRunErrorAtom,
  dryRunRetryAtom,
  uploadedFilesAtom,
} from './atoms';
import {
  BATCH_POLL_MS,
  LIST_POLL_MS,
  isBatchActive,
  resultsSummary,
} from './batch';
import type {
  BatchDefault,
  BatchListPage,
  BulkAssetBatch,
  DryRunResponse,
  FieldKey,
  UploadedFile,
  UploadResponse,
} from './types';

export const DRY_RUN_DEBOUNCE_MS = 400;
/** SpreadsheetParser's limits, checked here too so a bad file never posts. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];

export const defaultParams = ({ entityId, accountId }: BatchDefault) => ({
  defaultEntityId: entityId === null ? '' : String(entityId),
  defaultAccountId: accountId,
});

/**
 * The message to show for a failed request: the server's first error message
 * (e.g. the Salesforce permission reason), else a generic one.
 * @param {unknown} error - What the request threw
 * @param {string} fallback - Shown when the server said nothing usable
 * @return {Promise<string>} The message
 */
export const requestErrorMessage = async (error: unknown, fallback: string) => {
  const failure = await requestFailure(error);
  return (failure.kind !== 'rows' && failure.message) || fallback;
};

const isAbort = (error: unknown) =>
  error instanceof DOMException && error.name === 'AbortError';

/**
 * The browser-side file checks; the server repeats them.
 * @param {File} file - The chosen file
 * @return {string | null} Why the file cannot be uploaded, or null
 */
export const checkFile = (file: File) => {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (!file.name.includes('.') || !ACCEPTED_EXTENSIONS.includes(extension)) {
    return `Unsupported file type. Allowed: ${ACCEPTED_EXTENSIONS.join(', ')}`;
  }
  if (file.size > MAX_FILE_BYTES) return 'File too large (max 10MB)';
  return null;
};

let lastFileId = 0;

/**
 * Posts each file, one at a time, to the parse endpoint and appends its rows.
 * A file-level problem lists the file as failed and loads nothing.
 * @return {Function} Takes the files; resolves with each file's outcome
 */
export const useUploadFiles = () => {
  const store = useStore();
  const setFiles = useSetAtom(uploadedFilesAtom);
  const appendRows = useSetAtom(appendUploadedRowsAtom);

  return useCallback(
    async (files: File[]) => {
      const outcomes: UploadedFile[] = [];
      for (const file of files) {
        const id = `file-${(lastFileId += 1)}`;
        const update = (next: UploadedFile) => {
          setFiles((current) => {
            if (!current.some((entry) => entry.id === id)) {
              return [...current, next];
            }
            return current.map((entry) => (entry.id === id ? next : entry));
          });
          return next;
        };
        const base = { id, name: file.name, rowCount: 0, warnings: [] };
        const problem = checkFile(file);
        if (problem) {
          outcomes.push(
            update({ ...base, status: 'failed', errors: [problem] })
          );
          continue;
        }
        update({ ...base, status: 'uploading', errors: [] });

        const sentDefault = store.get(batchDefaultAtom);
        const body = new FormData();
        body.append('file', file);
        Object.entries(defaultParams(sentDefault)).forEach(([key, value]) =>
          body.append(key, value)
        );
        try {
          // Await the request itself, so an HTTP error rejects here and not
          // again on an unobserved promise.
          const response: UploadResponse = await (
            await api.post('crm/bulk_asset_batches/uploads', {
              body,
              timeout: 120_000,
            })
          ).json();
          if (response.fileErrors.length > 0) {
            outcomes.push(
              update({ ...base, status: 'failed', errors: response.fileErrors })
            );
            continue;
          }
          appendRows({
            fileId: id,
            results: response.rows,
            stale: store.get(batchDefaultAtom) !== sentDefault,
          });
          outcomes.push(
            update({
              ...base,
              status: 'loaded',
              rowCount: response.rows.length,
              errors: [],
              warnings: response.fileWarnings,
            })
          );
        } catch (error) {
          const message = await requestErrorMessage(
            error,
            'The file could not be uploaded. Try again.'
          );
          outcomes.push(
            update({ ...base, status: 'failed', errors: [message] })
          );
        }
      }
      return outcomes;
    },
    [store, setFiles, appendRows]
  );
};

/**
 * Keeps every row's validity current: after any edit, add or change of the
 * batch default, the rows waiting for an answer go to the dry-run endpoint
 * (debounced) and its answer replaces their errors and validity. A newer
 * change cancels the request in flight.
 */
export const useDryRunValidation = () => {
  const store = useStore();
  const pendingKey = useAtomValue(curationRowsAtom)
    .filter((row) => row.pending)
    .map((row) => `${row.clientId}:${row.revision}`)
    .join(',');
  const retry = useAtomValue(dryRunRetryAtom);
  const applyDryRun = useSetAtom(applyDryRunAtom);
  const setError = useSetAtom(dryRunErrorAtom);

  useEffect(() => {
    if (!pendingKey) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const pending = store.get(curationRowsAtom).filter((row) => row.pending);
      const sent = new Map(
        pending.map((row, index) => [
          index + 1,
          { clientId: row.clientId, revision: row.revision },
        ])
      );
      try {
        const response: DryRunResponse = await (
          await api.post('validations/crm/bulk_asset_batches', {
            json: {
              ...defaultParams(store.get(batchDefaultAtom)),
              rows: pending.map((row, index) => ({
                position: index + 1,
                values: row.values,
              })),
            },
            signal: controller.signal,
          })
        ).json();
        applyDryRun({ sent, results: response.rows });
        // A row the answer leaves out stays pending; say so rather than spin.
        const answered = new Set(response.rows.map((row) => row.position));
        setError(
          [...sent.keys()].every((position) => answered.has(position))
            ? null
            : 'Some rows could not be validated. Try again.'
        );
      } catch (error) {
        if (isAbort(error)) return;
        setError(
          await requestErrorMessage(
            error,
            'Rows could not be validated. Try again.'
          )
        );
      }
    }, DRY_RUN_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pendingKey, retry, store, applyDryRun, setError]);
};

const DEFAULT_LABELS: Record<string, string> = {
  defaultEntityId: 'Batch default Insights entity',
  defaultAccountId: 'Batch default CRM account',
};

/**
 * "Batch default Insights entity: Not found" for a 422 on the defaults.
 * @param {unknown} errors - The response's `errors`
 * @return {string | null} The lines joined, or null for another shape
 */
const defaultErrorsMessage = (errors: unknown) => {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return null;
  }
  const lines = Object.entries(errors as Record<string, unknown>)
    .filter(([key]) => DEFAULT_LABELS[key])
    .map(([key, message]) => `${DEFAULT_LABELS[key]}: ${message}`);
  return lines.length ? lines.join('. ') : null;
};

/** Why a submit or retry request failed, sorted by what the page does next. */
export type RequestFailure =
  /** 422 with per-row errors (`id` only on retry). */
  | {
      kind: 'rows';
      rows: Array<{
        id?: number;
        position: number;
        errors: Partial<Record<FieldKey, string>>;
      }>;
    }
  /** Anything else: network, 5xx, 403, 422 base or defaults. */
  | { kind: 'request'; message: string | null };

/**
 * Sorts a failed submit or retry. The server's own message is kept where it
 * has one (422 base, 403) so the banner can say what went wrong.
 * @param {unknown} error - What the request threw
 * @return {Promise<RequestFailure>} The failure
 */
export const requestFailure = async (
  error: unknown
): Promise<RequestFailure> => {
  if (!(error instanceof HTTPError)) return { kind: 'request', message: null };
  const body = await error.response.json().catch(() => null);
  const errors = body?.errors;
  if (Array.isArray(errors?.rows)) return { kind: 'rows', rows: errors.rows };
  const message: string | null =
    (Array.isArray(errors) ? errors[0]?.message : errors?.base) ??
    defaultErrorsMessage(errors) ??
    body?.error ??
    null;
  return { kind: 'request', message };
};

/** BulkAssetBatch::STATUS_EVENT, on the channel the show names. */
const STATUS_EVENT = 'crm-bulk-asset-batch-status-changed';

/**
 * Follows one batch: GET /crm/bulk_asset_batches/:id at once, every
 * BATCH_POLL_MS while it runs, and on each Pusher nudge on its channel. When
 * the page sees it finish, one toast gives the counts.
 * @param {number} id - The batch id
 * @return {object} The batch (null until loaded), a load error, and `replace`
 *   for a batch a retry answered with
 */
export const useBatch = (id: number) => {
  const [batch, setBatch] = useState<BulkAssetBatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const addNotifications = useSetAtom(addNotificationsAtom);
  // Only the newest request may land, so a slow poll never rolls the page back.
  const seq = useRef(0);
  const lastStatus = useRef<BulkAssetBatch['status'] | null>(null);

  const replace = useCallback((next: BulkAssetBatch) => {
    seq.current += 1;
    setBatch(next);
  }, []);

  const fetchBatch = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const next: BulkAssetBatch = await (
        await api.get(`crm/bulk_asset_batches/${id}`)
      ).json();
      if (mine !== seq.current) return;
      setBatch(next);
      setError(null);
    } catch (failure) {
      if (mine !== seq.current) return;
      setError(
        failure instanceof HTTPError && failure.response.status === 404
          ? 'This bulk upload does not exist.'
          : await requestErrorMessage(
              failure,
              'The bulk upload could not be loaded. Refresh the page to try again.'
            )
      );
      throw failure;
    }
  }, [id]);

  const active = !batch || isBatchActive(batch.status);
  // Polling gives up after repeated failures; a nudge that gets through while
  // the batch still runs starts it again.
  const { status: pollingStatus, restart } = usePolling({
    fn: fetchBatch,
    intervalMs: BATCH_POLL_MS,
    enabled: active,
  });
  useEffect(() => {
    if (pollingStatus === 'stopped' && batch && active && !error) restart();
  }, [pollingStatus, batch, active, error, restart]);

  const channelName = batch?.channel;
  useEffect(() => {
    if (!channelName) return undefined;
    const channel = subscribe(channelName);
    const nudge = () => fetchBatch().catch(() => {});
    channel.bind(STATUS_EVENT, nudge);
    return () => {
      channel.unbind(STATUS_EVENT, nudge);
      channel.unsubscribe();
    };
  }, [channelName, fetchBatch]);

  useEffect(() => {
    if (!batch) return;
    const finished =
      lastStatus.current !== null &&
      isBatchActive(lastStatus.current) &&
      !isBatchActive(batch.status);
    lastStatus.current = batch.status;
    if (!finished) return;
    addNotifications(NotificationGroup.BulkAssetBatch, [
      {
        id: `bulk-asset-batch-${batch.id}-${batch.completedAt}`,
        kind: batch.failedCount > 0 ? 'warning' : 'success',
        title: 'Bulk upload finished',
        message: resultsSummary(batch),
        autoHideMs: 10_000,
      },
    ]);
  }, [batch, addNotifications]);

  return { batch, error, replace, pollingStopped: pollingStatus === 'stopped' };
};

/**
 * One page of the Bulk uploads list. While a listed batch is pending or
 * processing it re-reads the page every LIST_POLL_MS, and re-renders only when
 * the answer changed.
 * @param {number} page - 1-based page
 * @param {number} perPage - Batches per page
 * @return {object} The page (null until the first answer), the load error, and whether a load is in flight
 */
export const useBatchList = (page: number, perPage: number) => {
  const [list, setList] = useState<BatchListPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Only the newest request may land, so a slow poll never shows a stale page.
  const seq = useRef(0);
  const fingerprint = useRef('');

  const fetchList = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const next = await api
        .get('crm/bulk_asset_batches', {
          searchParams: { page, per_page: perPage },
        })
        .json<BatchListPage>();
      if (mine !== seq.current) return;
      const print = JSON.stringify(next);
      if (print !== fingerprint.current) {
        fingerprint.current = print;
        setList(next);
      }
      setError(null);
      setLoading(false);
    } catch (failure) {
      if (mine !== seq.current) return;
      setError(
        await requestErrorMessage(
          failure,
          'The bulk uploads could not be loaded. Refresh the page to try again.'
        )
      );
      setLoading(false);
      throw failure;
    }
  }, [page, perPage]);

  useEffect(() => {
    setLoading(true);
    fetchList().catch(() => {});
  }, [fetchList]);

  const active = !!list?.batches.some((batch) => isBatchActive(batch.status));
  // Polling gives up after repeated failures; a later load that gets through
  // (the user moving page) starts it again while a batch still runs.
  const { status: pollingStatus, restart } = usePolling({
    fn: fetchList,
    intervalMs: LIST_POLL_MS,
    enabled: active,
    immediate: false,
  });
  useEffect(() => {
    if (pollingStatus === 'stopped' && active && !error) restart();
  }, [pollingStatus, active, error, restart]);

  return { list, error, loading };
};
